import { Anchor, List, Text } from '@mantine/core';

import { LegalPage, LegalSection } from '@/components/organisms';

export function DpaPage() {
  return (
    <LegalPage
      title="Data Processing Agreement"
      intro="This Data Processing Agreement (DPA) sets out the terms on which Goldseam AI Consulting Ltd processes personal data on behalf of users of Assay. It forms part of the Terms of Service. By using Assay to process contact data, you (the data controller) agree to this DPA."
    >
      <LegalSection heading="1. Parties">
        <Text size="sm">
          <strong>Data Controller:</strong> You, the registered user of Assay, acting in your
          personal or business capacity.
        </Text>
        <Text size="sm">
          <strong>Data Processor:</strong> Goldseam AI Consulting Ltd, registered in England &
          Wales, company number 16896682 ("we", "us", "Assay").
        </Text>
      </LegalSection>

      <LegalSection heading="2. Subject matter and duration">
        <Text size="sm">
          We process personal data on your behalf for the purpose of providing the Assay service:
          storing, displaying, and using contact data to generate outreach messages and maintain
          relationship records. This DPA applies for the duration of your use of Assay and until all
          personal data is deleted following account closure.
        </Text>
      </LegalSection>

      <LegalSection heading="3. Nature and purpose of processing">
        <Text size="sm">We process contact data to:</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Store and display contacts, grades, and outreach history in the app.
          </List.Item>
          <List.Item>
            Generate personalised outreach message drafts using AI (Anthropic API).
          </List.Item>
          <List.Item>
            Enrich contact data via third-party enrichment providers, where you have enabled that
            feature.
          </List.Item>
          <List.Item>
            Execute background tasks related to contact management via Trigger.dev.
          </List.Item>
        </List>
      </LegalSection>

      <LegalSection heading="4. Types of personal data processed">
        <Text size="sm">
          The personal data processed may include, depending on what you import:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>Names, email addresses, phone numbers.</List.Item>
          <List.Item>Job titles, company names, LinkedIn URLs.</List.Item>
          <List.Item>Notes and relationship context you have recorded about contacts.</List.Item>
          <List.Item>Outreach message history.</List.Item>
          <List.Item>Relationship grade/tier assigned by you.</List.Item>
        </List>
        <Text size="sm">
          You must not import special category data (as defined by UK GDPR Article 9) without
          explicit consent from the data subjects.
        </Text>
      </LegalSection>

      <LegalSection heading="5. Our obligations as data processor">
        <Text size="sm">We will:</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Process personal data only on your documented instructions (i.e. by providing the
            service as described) unless required to do otherwise by applicable law.
          </List.Item>
          <List.Item>
            Ensure all personnel with access to personal data are bound by appropriate
            confidentiality obligations.
          </List.Item>
          <List.Item>
            Implement appropriate technical and organisational security measures to protect personal
            data against unauthorised access, loss, or destruction.
          </List.Item>
          <List.Item>
            Not engage a new sub-processor without informing you and giving you the opportunity to
            object. Our current sub-processors are listed in the{' '}
            <Anchor href="/privacy" size="sm">
              Privacy Policy
            </Anchor>
            .
          </List.Item>
          <List.Item>
            Assist you in responding to data subject rights requests (access, deletion, portability,
            rectification) relating to personal data we process on your behalf, where technically
            feasible. Contact{' '}
            <Anchor href="mailto:privacy@runassay.app" size="sm">
              privacy@runassay.app
            </Anchor>
            .
          </List.Item>
          <List.Item>
            Notify you without undue delay (and no later than 72 hours) after becoming aware of a
            personal data breach involving data we process on your behalf.
          </List.Item>
          <List.Item>
            Delete or return all personal data on termination of the service, in accordance with the
            retention periods set out in the{' '}
            <Anchor href="/privacy" size="sm">
              Privacy Policy
            </Anchor>
            .
          </List.Item>
          <List.Item>
            ke available all information reasonably necessary to demonstrate compliance with this
            DPA, and allow for and contribute to audits and inspections by you or an auditor you
            appoint. We may satisfy audit requests via provision of current third-party audit
            reports (e.g. SOC 2 Type II) in the first instance.
          </List.Item>
        </List>
      </LegalSection>

      <LegalSection heading="6. Your obligations as data controller">
        <Text size="sm">You confirm that:</Text>
        <List size="sm" spacing={4}>
          <List.Item>
            You have a lawful basis under UK GDPR for processing the personal data you import into
            Assay.
          </List.Item>
          <List.Item>
            Where required, you have provided appropriate privacy notices to your contacts informing
            them their data may be processed via software tools including AI services.
          </List.Item>
          <List.Item>
            You will handle any data subject rights requests from your contacts promptly.
          </List.Item>
          <List.Item>You will not import data that you do not have the right to process.</List.Item>
        </List>
      </LegalSection>

      <LegalSection heading="7. Sub-processors">
        <Text size="sm">
          We use the following sub-processors to deliver the service. By accepting this DPA you
          authorise their use:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            <strong>Clerk</strong> - authentication (user account data only; not contact data).
          </List.Item>
          <List.Item>
            <strong>Neon</strong> - database storage (all contact and account data).
          </List.Item>
          <List.Item>
            <strong>Anthropic</strong> - AI message generation (contact names, notes, context
            included in API prompts).
          </List.Item>
          <List.Item>
            <strong>Trigger.dev</strong> - background job processing (contact identifiers in task
            payloads).
          </List.Item>
          <List.Item>
            <strong>Netlify</strong> - serverless function hosting (request/response data in
            transit).
          </List.Item>
        </List>
        <Text size="sm">
          We will update this list if we add new sub-processors and will give you reasonable notice
          before doing so.
        </Text>
      </LegalSection>

      <LegalSection heading="8. International data transfers">
        <Text size="sm">
          Some sub-processors (including Anthropic and Trigger.dev) may process data in the United
          States. Where data is transferred outside the UK, we ensure appropriate safeguards are in
          place - such as the UK International Data Transfer Agreement (IDTA) or the UK extension to
          the EU Standard Contractual Clauses (SCCs) - or we rely on adequacy decisions where
          available.
        </Text>
      </LegalSection>

      <LegalSection heading="9. Security measures">
        <Text size="sm">We maintain the following technical and organisational measures:</Text>
        <List size="sm" spacing={4}>
          <List.Item>Encryption in transit (TLS 1.2+) for all data transfers.</List.Item>
          <List.Item>Encryption at rest for database storage.</List.Item>
          <List.Item>
            Access controls: production data is accessible only to authorised personnel on a
            need-to-know basis.
          </List.Item>
          <List.Item>Regular security reviews and dependency updates.</List.Item>
          <List.Item>Authentication enforced via Clerk (including MFA support).</List.Item>
        </List>
      </LegalSection>

      <LegalSection heading="10. Governing law">
        <Text size="sm">
          This DPA is governed by the laws of England & Wales. Any disputes shall be subject to the
          exclusive jurisdiction of the courts of England & Wales. This DPA shall be read alongside
          the{' '}
          <Anchor href="/terms" size="sm">
            Terms of Service
          </Anchor>{' '}
          and{' '}
          <Anchor href="/privacy" size="sm">
            Privacy Policy
          </Anchor>
          .
        </Text>
      </LegalSection>

      <Text size="sm" c="dimmed">
        Last updated: 22 March 2026. Goldseam AI Consulting Ltd, company number 16896682, registered
        in England & Wales. Contact:{' '}
        <Anchor href="mailto:privacy@runassay.app" size="sm">
          privacy@runassay.app
        </Anchor>
      </Text>
    </LegalPage>
  );
}
